<ng-container *ngIf="chatbotEnabled">

<!-- ── FAB ────────────────────────────────────────────────────── -->
<button
  class="chatbot-fab"
  [class.chatbot-fab--hidden]="isOpen"
  [attr.aria-hidden]="isOpen ? true : null"
  [attr.tabindex]="isOpen ? -1 : null"
  (click)="toggleChat()"
  [attr.title]="'shared-components.chatbot.aria-open' | translate"
  [attr.aria-label]="'shared-components.chatbot.aria-open' | translate"
>
  <svg class="fab-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z"/>
  </svg>
</button>

<!-- ── Panel ──────────────────────────────────────────────────── -->
<div
  class="chatbot-panel"
  [class.open]="isOpen"
  [class.chatbot-panel--welcome]="showWelcome"
  role="dialog"
  [attr.aria-label]="'shared-components.chatbot.assistant-name' | translate"
>

  <!-- ── WELCOME STATE ────────────────────────────────────────── -->
  <ng-container *ngIf="showWelcome">

    <div class="welcome-topbar">
      <button class="topbar-btn" (click)="toggleChat()" [attr.aria-label]="'shared-components.chatbot.aria-close' | translate">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>

    <div class="welcome-body">
      <!-- Nolly en loop hasta que se manda el primer mensaje (el welcome se
           desmonta ahí). El video ya viene sin pista de audio; `muted` igual es
           obligatorio para que el navegador permita el autoplay.
           El <img> de abajo es el fallback para prefers-reduced-motion (el CSS
           conmuta cual de los dos se ve). -->
      <div class="character-wrapper">
        <video
          class="character-video"
          autoplay
          loop
          muted
          [muted]="true"
          playsinline
          preload="auto"
          poster="assets/nolia/character/nolly-poster.jpg"
          aria-hidden="true"
        >
          <source src="assets/nolia/character/nolly.mp4" type="video/mp4">
        </video>
        <img
          class="character-img"
          src="assets/nolia/character/nolly-poster.jpg"
          alt=""
          width="154"
          height="190"
          aria-hidden="true"
        />
      </div>

      <div class="welcome-text">
        <h2 class="welcome-title">{{ 'shared-components.chatbot.welcome-title' | translate }}</h2>
        <p class="welcome-subtitle">{{ 'shared-components.chatbot.welcome-subtitle' | translate }}</p>
      </div>
    </div>

  </ng-container>

  <!-- ── CHAT STATE ───────────────────────────────────────────── -->
  <ng-container *ngIf="!showWelcome">

    <div class="chatbot-header">
      <div class="header-info">
        <span class="header-name">Nolia AI</span>
        <span class="header-status">
          <span class="status-dot"></span>
          ONLINE · {{ roleLabel }}
        </span>
      </div>
      <div class="header-actions">
        <button
          class="header-btn"
          (click)="startNewConversation()"
          [disabled]="isLoading"
          [attr.title]="'shared-components.chatbot.new-conversation' | translate"
          [attr.aria-label]="'shared-components.chatbot.new-conversation' | translate"
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
          </svg>
        </button>
        <button class="header-btn" (click)="toggleChat()" [attr.aria-label]="'shared-components.chatbot.aria-close' | translate">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
    </div>

    <div class="chatbot-messages" #messagesContainer role="log" aria-live="polite">

      <div class="date-separator" aria-hidden="true"><span>HOY</span></div>

      <ng-container *ngFor="let msg of messages">

        <div class="message-row message-row--user" *ngIf="msg.role === 'user'">
          <div class="message-bubble message-bubble--user">{{ msg.content }}</div>
          <div class="message-meta">{{ userRoleLabel }} · {{ msg.timestamp | date:'HH:mm' }}</div>
        </div>

        <div class="message-row message-row--assistant" *ngIf="msg.role === 'assistant'">
          <div class="message-avatar" aria-hidden="true"><img src="assets/nolia/character/nolly-seat.png" alt="" width="28" height="28"></div>
          <div class="message-bubble message-bubble--assistant" [innerHTML]="msg.content | markdown"></div>
        </div>

      </ng-container>

      <div class="message-row message-row--assistant" *ngIf="isLoading">
        <div class="message-avatar" aria-hidden="true"><img src="assets/nolia/character/nolly-seat.png" alt="" width="28" height="28"></div>
        <div class="message-bubble message-bubble--assistant message-bubble--loading">
          <span class="loading-dot"></span>
          <span class="loading-dot"></span>
          <span class="loading-dot"></span>
        </div>
      </div>

    </div>

    <div class="error-banner" *ngIf="errorMessage" role="alert">
      <span>{{ errorMessage }}</span>
      <button class="error-close" (click)="dismissError()" [attr.aria-label]="'shared-components.chatbot.aria-close-error' | translate">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>

  </ng-container>

  <!-- ── Footer + input (siempre visible) ─────────────────────── -->
  <div class="chatbot-footer">
    <div class="chatbot-input-area">
      <textarea
        #messageInput
        [(ngModel)]="inputText"
        (keydown)="onKeyDown($event)"
        [disabled]="isLoading"
        [placeholder]="inputPlaceholder"
        maxlength="4000"
        rows="1"
        [attr.aria-label]="'shared-components.chatbot.aria-message-input' | translate"
      ></textarea>
      <button
        class="send-btn"
        (click)="sendMessage()"
        [disabled]="isLoading || !inputText.trim()"
        [attr.aria-label]="'shared-components.chatbot.aria-send' | translate"
      >
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
        </svg>
      </button>
    </div>
    <p class="powered-by">{{ 'shared-components.chatbot.powered-by' | translate }}</p>
  </div>

</div>

</ng-container>
